/**
 * Browser side of the push pipeline.
 *
 * Responsibilities, in the order they matter:
 *   1. subscribe this browser and tell our server about it
 *   2. re-sync on every launch, because subscriptions expire quietly
 *
 * Step 2 is the one that keeps this working over months. A push subscription
 * can be revoked by the browser, the OS, or an app reinstall, and nothing
 * informs the page. Re-reading and re-uploading it at launch costs one request
 * and prevents the "it worked in June, it stopped in July" failure.
 */

import { api } from './api';
import { getPlatformInfo } from './platform';

/** VAPID keys travel as base64url but `subscribe` wants raw bytes. */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  // Backed by a plain ArrayBuffer so it satisfies BufferSource, which excludes
  // SharedArrayBuffer-backed views.
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

export interface EnableResult {
  ok: boolean;
  subscriptionId?: string;
  /** Machine-readable reason, for the diagnostics UI. */
  reason?: 'blocked' | 'denied' | 'dismissed' | 'failed';
  message?: string;
}

export async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

export async function getExistingSubscription(): Promise<PushSubscription | null> {
  const registration = await getRegistration();
  if (!registration) return null;
  try {
    return await registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Turn notifications on for this device.
 *
 * Must be called from a real user gesture: Safari rejects a permission prompt
 * that is not tied to a tap, and does so without a useful error.
 */
export async function enablePush(): Promise<EnableResult> {
  const platform = getPlatformInfo();

  if (!platform.canEnablePush) {
    return { ok: false, reason: 'blocked', message: platform.blocker };
  }

  const permission = await Notification.requestPermission();

  if (permission === 'denied') {
    return {
      ok: false,
      reason: 'denied',
      message: 'Notifications were declined. They have to be re-enabled in device settings.',
    };
  }

  if (permission !== 'granted') {
    return { ok: false, reason: 'dismissed', message: 'The permission prompt was dismissed.' };
  }

  try {
    const registration = await getRegistration();
    if (!registration) {
      return { ok: false, reason: 'failed', message: 'The app is still starting up. Try again.' };
    }

    const { publicKey } = await api.vapidKey();

    // Reuse an existing subscription if there is one; subscribing twice with
    // the same key is fine, but this avoids a needless round trip.
    const existing = await registration.pushManager.getSubscription();
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        // Required, and on Chrome a subscription without it is rejected.
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      }));

    const { subscriptionId } = await api.subscribePush(
      subscription.toJSON(),
      describePlatform(),
    );

    return { ok: true, subscriptionId };
  } catch (error) {
    return {
      ok: false,
      reason: 'failed',
      message: error instanceof Error ? error.message : 'Could not set up notifications.',
    };
  }
}

/**
 * Re-upload the current subscription if there is one. Cheap, idempotent, and
 * called on every launch — this is what stops devices going quietly deaf.
 */
export async function syncSubscription(): Promise<string | null> {
  try {
    const subscription = await getExistingSubscription();
    if (!subscription) return null;

    const { subscriptionId } = await api.subscribePush(
      subscription.toJSON(),
      describePlatform(),
    );
    return subscriptionId;
  } catch {
    return null;
  }
}

export async function disablePush(): Promise<void> {
  const subscription = await getExistingSubscription();
  if (!subscription) return;

  const endpoint = subscription.endpoint;
  try {
    await subscription.unsubscribe();
  } finally {
    await api.unsubscribePush(endpoint).catch(() => undefined);
  }
}

function describePlatform(): string {
  const info = getPlatformInfo();
  if (info.isIOS) return info.isStandalone ? 'ios-standalone' : 'ios-browser';
  if (info.isAndroid) return 'android';
  return 'desktop';
}
